import type { User } from 'firebase/auth';
import { parsePhoneNumberFromString, type CountryCode } from 'libphonenumber-js';
import { capitalizeWords } from './textFormat';

export interface AuthProfilePrefill {
  fullName?: string;
  phone?: string;
  phoneCountry?: string;
}

function googleProvider(user: User) {
  return user.providerData.find((p) => p.providerId === 'google.com');
}

function parseAuthPhone(raw: string | null | undefined): Pick<AuthProfilePrefill, 'phone' | 'phoneCountry'> {
  if (!raw?.trim()) return {};

  const trimmed = raw.trim();
  const parsed = trimmed.startsWith('+')
    ? parsePhoneNumberFromString(trimmed)
    : parsePhoneNumberFromString(trimmed, 'PK');

  if (parsed?.isValid()) {
    return {
      phone: parsed.format('E.164'),
      phoneCountry: parsed.country ?? 'PK',
    };
  }

  return { phone: trimmed, phoneCountry: 'PK' };
}

/**
 * Best-effort prefill from Firebase Auth (Google display name, linked phone if any).
 * Google Sign-In usually does not expose phone; Firebase `phoneNumber` is set for phone-auth users.
 */
export function getAuthProfilePrefill(user: User | null): AuthProfilePrefill {
  if (!user) return {};

  const result: AuthProfilePrefill = {};

  const rawName =
    user.displayName?.trim() ||
    googleProvider(user)?.displayName?.trim() ||
    '';

  if (rawName) {
    result.fullName = capitalizeWords(rawName);
  }

  const rawPhone =
    user.phoneNumber?.trim() ||
    googleProvider(user)?.phoneNumber?.trim() ||
    '';

  if (rawPhone) {
    Object.assign(result, parseAuthPhone(rawPhone));
  }

  return result;
}
